import React, { useState, useEffect } from 'react';
import { dashboardAPI, adminAPI } from '../api/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  AlertCircle,
  Calendar,
  CheckCircle,
  DollarSign,
  CreditCard,
  Upload,
} from 'lucide-react';

const Dashboard = () => {
  const { user, isPartner, isManager, isStaff } = useAuth();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    try {
      const response = await dashboardAPI.getMetrics();
      setMetrics(response.data);
    } catch (error) {
      console.error('Failed to fetch metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImportData = async () => {
    if (!window.confirm('This will replace ALL existing data with the default dataset (37 clients, 39 tasks). Continue?')) {
      return;
    }
    setImporting(true);
    try {
      const response = await adminAPI.importData();
      alert(`Data imported successfully!\n- Clients: ${response.data.clients_imported}\n- Tasks: ${response.data.tasks_imported}`);
      fetchMetrics();
    } catch (error) {
      alert('Failed to import data: ' + (error.response?.data?.detail || error.message));
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const stats = [
    {
      name: 'Active Tasks',
      value: metrics?.total_active_tasks || 0,
      icon: TrendingUp,
      color: 'bg-blue-500',
      textColor: 'text-blue-600',
      bgColor: 'bg-blue-50',
      filter: 'all',
    },
    {
      name: 'Overdue Tasks',
      value: metrics?.overdue_tasks || 0,
      icon: AlertCircle,
      color: 'bg-red-500',
      textColor: 'text-red-600',
      bgColor: 'bg-red-50',
      filter: 'overdue',
    },
    {
      name: 'Due Today',
      value: metrics?.due_today || 0,
      icon: Calendar,
      color: 'bg-yellow-500',
      textColor: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      filter: 'today',
    },
    {
      name: 'Due This Week',
      value: metrics?.due_this_week || 0,
      icon: Calendar,
      color: 'bg-orange-500',
      textColor: 'text-orange-600',
      bgColor: 'bg-orange-50',
      filter: 'week',
    },
    {
      name: 'Completed This Month',
      value: metrics?.completed_this_month || 0,
      icon: CheckCircle,
      color: 'bg-green-500',
      textColor: 'text-green-600',
      bgColor: 'bg-green-50',
      filter: 'completed',
    },
    // Invoiced and Receipt cards - only for Partner/Manager
    ...(isStaff ? [] : [
      {
        name: 'Invoiced (Pending Receipt)',
        value: `₹${(metrics?.invoiced_pending_receipt || 0).toLocaleString()}`,
        icon: DollarSign,
        color: 'bg-purple-500',
        textColor: 'text-purple-600',
        bgColor: 'bg-purple-50',
        filter: 'invoiced',
      },
      {
        name: 'Receipts This Month',
        value: `₹${(metrics?.total_receipts_this_month || 0).toLocaleString()}`,
        icon: CreditCard,
        color: 'bg-indigo-500',
        textColor: 'text-indigo-600',
        bgColor: 'bg-indigo-50',
        filter: 'paid',
      },
    ]),
  ];

  return (
    <div className="space-y-6" data-testid="dashboard">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Welcome back, {user?.full_name}!</h2>
          <p className="text-gray-600 mt-1">Here's what's happening with your tasks today.</p>
        </div>
        {/* Import Data button disabled - uncomment if needed
        {isPartner && (
          <button
            onClick={handleImportData}
            disabled={importing}
            className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            data-testid="import-data-button"
          >
            <Upload className="w-4 h-4 mr-2" />
            {importing ? 'Importing...' : 'Import Data'}
          </button>
        )}
        */}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {stats.map((stat) => (
          <div
            key={stat.name}
            onClick={() => {
              if (['all', 'overdue', 'today', 'week'].includes(stat.filter)) {
                navigate(`/tasks?filter=${stat.filter}`);
              } else if (['completed', 'invoiced', 'paid'].includes(stat.filter)) {
                navigate(`/billing?filter=${stat.filter}`);
              }
            }}
            className={`bg-white rounded-lg shadow-sm p-6 border border-gray-200 hover:shadow-md transition-shadow cursor-pointer`}
            data-testid={`metric-${stat.name.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <p className={`text-2xl font-bold mt-2 ${stat.textColor}`}>{stat.value}</p>
              </div>
              <div className={`${stat.bgColor} p-3 rounded-lg`}>
                <stat.icon className={`w-6 h-6 ${stat.textColor}`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <a
            href="/tasks"
            className="flex items-center p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
          >
            <TrendingUp className="w-8 h-8 text-blue-600 mr-3" />
            <div>
              <p className="font-medium text-gray-900">View Tasks</p>
              <p className="text-sm text-gray-600">Manage all your tasks</p>
            </div>
          </a>
          {!isStaff && (
            <a
              href="/clients"
              className="flex items-center p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
            >
              <CheckCircle className="w-8 h-8 text-green-600 mr-3" />
              <div>
                <p className="font-medium text-gray-900">View Clients</p>
                <p className="text-sm text-gray-600">Manage client data</p>
              </div>
            </a>
          )}
          {(user?.role === 'partner' || user?.role === 'manager') && (
            <a
              href="/reports"
              className="flex items-center p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
            >
              <DollarSign className="w-8 h-8 text-purple-600 mr-3" />
              <div>
                <p className="font-medium text-gray-900">View Reports</p>
                <p className="text-sm text-gray-600">Access detailed reports</p>
              </div>
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
